import { i18n } from '@lingui/core';
import type {
    Result,
    ResultGroup,
    Suggestion,
} from '@schibsted-nmp/search-frontend-clients/suggest';
import React from 'react';
import { useAppConfig } from '../AppConfig';
import { countSymbols } from '../common/countSymbols';
import { AdResults, adResultsAction } from '../results/AdResults';
import {
    CommandResults,
    commandResultsAction,
} from '../results/CommandResults';
import {
    CompanyProfileResults,
    companyProfileResultsAction,
} from '../results/CompanyProfileResults';
import {
    DefaultResults,
    defaultResultsAction,
} from '../results/DefaultResults';
import {
    FALLBACK_RESULT,
    GlobalSearchResults,
    globalSearchResultsAction,
} from '../results/GlobalSearchResults';
import {
    LastSearchResults,
    lastSearchResultsAction,
} from '../results/LastSearchResults';
import {
    PopularSearchResults,
    popularSearchResultsAction,
} from '../results/PopularSearchResults';
import {
    SelectAction,
    type ActionContext,
    type ResultsProps,
} from '../results/ResultTypes';
import {
    SavedSearchResults,
    savedSearchResultsAction,
} from '../results/SavedSearchResults';
import { Searchbox } from '../searchbox/Searchbox';
import { SearchboxOption } from '../searchbox/SearchboxOption';
import { useSuggestionsData } from './SuggestionsData';

type SuggestionsProps = {
    isProbablyMobile: boolean;
    searchPlaceholder: string;
    searchTerm: string | null;
};

export function Suggestions(props: SuggestionsProps) {
    const { isProbablyMobile, searchPlaceholder, searchTerm } = props;

    const config = useAppConfig();
    const { actionUrl, siteUrl, where } = config;

    const [query, setQuery] = React.useState<string | null>(searchTerm || null);
    const { suggestionsData } = useSuggestionsData(query);

    const fetchEmptySearchOnInitialFocus = React.useCallback(() => {
        setQuery((prevValue) => (prevValue === null ? '' : prevValue));
    }, [setQuery]);

    const { currentLastSearchResults, clearCurrentLastSearchResults } =
        useCurrentLastSearchResults(query);

    const handleChange = React.useCallback(
        (value: string) => {
            setQuery(value);
        },
        [setQuery],
    );

    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleSelect = React.useCallback(
        (result: Result) => {
            if (!suggestionsData) {
                return false;
            }

            // Read the input value from the input element, not from the state.
            // This is important because the search form may be submitted by the
            // user before the state is updated, eg. by typing very fast and
            // hitting Enter.
            const inputValue = inputRef.current?.value ?? '';

            const context: ActionContext = {
                config,
                metaData: suggestionsData.metaData,
                result,
                value: inputValue,
            };

            let selectAction = SelectAction.CLOSE;
            switch (result.resultType) {
                case 'AD':
                    selectAction = adResultsAction(context);
                    break;
                case 'COMMAND':
                    selectAction = commandResultsAction(context);
                    break;
                case 'COMPANYPROFILE':
                    selectAction = companyProfileResultsAction(context);
                    break;
                case 'LASTSEARCHES':
                    selectAction = lastSearchResultsAction(context);
                    if (selectAction === SelectAction.KEEP_OPEN) {
                        clearCurrentLastSearchResults();
                    }
                    break;
                case 'POPULAR':
                    selectAction = popularSearchResultsAction(context);
                    break;
                case 'SAVEDSEARCH':
                    selectAction = savedSearchResultsAction(context);
                    break;
                case 'SEARCH_FINN':
                    selectAction = globalSearchResultsAction(context);
                    break;
                default:
                    selectAction = defaultResultsAction(context);
                    break;
            }

            const keepPopoverOpen = selectAction === SelectAction.KEEP_OPEN;
            return keepPopoverOpen;
        },
        [clearCurrentLastSearchResults, config, inputRef, suggestionsData],
    );

    const getAccessibilityHeading = React.useCallback(() => {
        const totalHits =
            suggestionsData?.results.reduce(
                (prev, curr) => prev + curr.results.length,
                0,
            ) ?? 0;
        return (
            <h2 className="sr-only" role="status">
                {i18n.t({
                    id: 'suggestions.status',
                    message: '{count} suggestions',
                    values: { count: totalHits },
                })}
            </h2>
        );
    }, [suggestionsData]);

    const getDefaultOption = React.useCallback(() => {
        const defaultResult = getDefaultResult(suggestionsData);
        return defaultResult ? (
            <SearchboxOption className="hidden" value={defaultResult} />
        ) : null;
    }, [suggestionsData]);

    function renderRegularResultGroup(resultGroup: ResultGroup) {
        const headingClass = 'text-xs s-text-subtle antialiased';
        const context: ResultsProps = {
            headingClass,
            resultGroup,
        };

        const { groupingType } = resultGroup;
        const key = createResultGroupKey(resultGroup, suggestionsData);

        switch (groupingType) {
            case 'AD':
                return <AdResults key={key} {...context} />;
            case 'COMMAND':
                return <CommandResults key={key} {...context} />;
            case 'COMPANYPROFILE':
                return where === 'BROWSE' ? (
                    <CompanyProfileResults key={key} {...context} />
                ) : (
                    <DefaultResults key={key} {...context} />
                );
            case 'SEARCH_FINN':
                return <GlobalSearchResults key={key} {...context} />;
            default:
                return <DefaultResults key={key} {...context} />;
        }
    }

    function renderNoTermResultGroup(resultGroup: ResultGroup) {
        const headingClass = 'h4 mb-12 s-text';
        const context: ResultsProps = {
            headingClass,
            resultGroup,
        };

        const { groupingType } = resultGroup;
        const key = createResultGroupKey(resultGroup, suggestionsData);

        switch (groupingType) {
            case 'LASTSEARCHES': {
                const contextWithCurrentLastSearchResults = {
                    ...context,
                    resultGroup: {
                        ...context.resultGroup,
                        results: currentLastSearchResults,
                    },
                };
                return (
                    <LastSearchResults
                        key={key}
                        {...contextWithCurrentLastSearchResults}
                    />
                );
            }
            case 'POPULAR':
                return <PopularSearchResults key={key} {...context} />;
            case 'SAVEDSEARCH':
                return <SavedSearchResults key={key} {...context} />;
        }
    }

    const originalQueryLength = suggestionsData
        ? countSymbols(suggestionsData.metaData.originalQuery ?? '')
        : 0;
    const isNoTermSearch = originalQueryLength < 2;
    const hasNoTermResults =
        isNoTermSearch && (where === 'FRONTPAGE' || where === 'BROWSE');

    const { isLandingPage } = useAppConfig();

    return (
        <form action={`${siteUrl}${actionUrl}`} role="search">
            <Searchbox
                accessibilityHeading={getAccessibilityHeading}
                defaultOption={getDefaultOption}
                defaultValue={getDefaultResult(suggestionsData)}
                displayValue={() => query ?? ''}
                isModal={isProbablyMobile && !isLandingPage}
                isStatic={isLandingPage}
                name="q"
                onChange={handleChange}
                onFocus={fetchEmptySearchOnInitialFocus}
                onSelect={handleSelect}
                openOnFocus
                placeholder={searchPlaceholder}
                ref={inputRef}
            >
                {hasNoTermResults
                    ? suggestionsData?.results.map(renderNoTermResultGroup)
                    : suggestionsData?.results.map(renderRegularResultGroup)}
            </Searchbox>
        </form>
    );
}

function useCurrentLastSearchResults(searchTerm: string | null) {
    const { suggestionsData } = useSuggestionsData(searchTerm);

    const [currentLastSearchResults, setCurrentLastSearchResults] =
        React.useState<Result[]>([]);

    React.useEffect(
        function updateCurrentLastSearchResults() {
            const lastSearchResultGroup = suggestionsData?.results.find(
                (resultGroup) => resultGroup.groupingType === 'LASTSEARCHES',
            );
            setCurrentLastSearchResults(
                lastSearchResultGroup ? lastSearchResultGroup.results : [],
            );
        },
        [setCurrentLastSearchResults, suggestionsData],
    );

    const clearCurrentLastSearchResults = React.useCallback(
        () => setCurrentLastSearchResults([]),
        [setCurrentLastSearchResults],
    );

    return { currentLastSearchResults, clearCurrentLastSearchResults };
}

function getDefaultResult(suggestionsData: Suggestion | null) {
    const chooseMeResult = suggestionsData?.results
        .map((resultGroup) =>
            resultGroup.results.find((result) => result.chooseMe),
        )
        .find(Boolean);

    const globalSearchResult = suggestionsData?.results
        .filter((resultGroup) => resultGroup.groupingType === 'SEARCH_FINN')
        .flatMap((resultGroup) => resultGroup.results)
        .find(Boolean);

    return chooseMeResult ?? globalSearchResult ?? FALLBACK_RESULT;
}

function createResultGroupKey(
    resultGroup: ResultGroup,
    suggestionsData: Suggestion | null,
) {
    const groupingType = resultGroup.groupingType;
    const order = [...resultGroup.results].pop()?.order ?? '';
    const originalQuery = suggestionsData?.metaData.originalQuery ?? '';
    return `${originalQuery}-${groupingType}-${order}`;
}
